import * as Dialog from '@radix-ui/react-dialog'
import { Button, cn } from '@merels/ui'
import { useSignals } from '@preact/signals-react/runtime'
import type { RoomViewVMStartGameSetupVM } from '../../../deps/ui/playAreaVM/playAreaVM.types'

export interface StartGameSetupDialogProps {
  startGameSetup: RoomViewVMStartGameSetupVM
  /**
   * Used only for styling (shared-screen borders in local mode).
   * Keep this out of the view model to avoid leaking UI styling concerns.
   */
  isLocalSharedScreen: boolean
}

export function StartGameSetupDialog({ startGameSetup, isLocalSharedScreen }: StartGameSetupDialogProps) {
  useSignals()

  const open = startGameSetup.isOpen.value
  const phase = startGameSetup.phase.value

  if (!open) return null

  const slotCountOptions = startGameSetup.slotCountOptions.value
  const focusedPlayerCountIndex = startGameSetup.focusedPlayerCountIndex.value
  const groups = startGameSetup.groups.value
  const selectedMateId = startGameSetup.selectedMateId.value
  const selfMateId = startGameSetup.selfMateId.value

  const renderMateBadge = (mateId: string) => {
    const isSelected = selectedMateId === mateId
    const name = startGameSetup.getMateDisplayName(mateId)

    const isMe = mateId === selfMateId
    const local = startGameSetup.isMateLocallyControlled(mateId)

    // In shared mode, selection is controller-only; click has no effect.
    const handleBadgeClick = isLocalSharedScreen
      ? undefined
      : (e: React.MouseEvent) => {
          e.stopPropagation()
          startGameSetup.setSelectedMateId(mateId)
        }

    return (
      <span
        key={mateId}
        className={cn(
          'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium',
          'bg-muted-bg text-primary',
          isLocalSharedScreen ? 'border-2 border-basic' : 'border-0',
          local && 'border-2 border-basic',
          isMe && 'border-4 border-accent',
          isSelected && 'ring-2 ring-focus',
        )}
        onClick={handleBadgeClick}
        style={{ cursor: handleBadgeClick ? 'pointer' : 'default' }}
      >
        {name}
      </span>
    )
  }

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => (nextOpen ? undefined : startGameSetup.cancel())}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-overlay z-[10000]" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[10000] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-basic bg-surface p-6 shadow-lg',
            'text-primary'
          )}
        >
          {phase === 'choosePlayerCount' && (
            <>
              <Dialog.Title className="text-lg font-bold text-primary mb-6">Select Number of Players</Dialog.Title>
              <div className="grid grid-cols-2 gap-3 justify-items-center">
                {slotCountOptions.map((count, idx) => {
                  const isFocused = idx === focusedPlayerCountIndex
                  return (
                    <Button
                      key={count}
                      className={cn(
                        'w-24 justify-center',
                        isFocused && 'ring-2 ring-focus ring-offset-2 ring-offset-surface'
                      )}
                      onClick={() => void startGameSetup.selectPlayerCount(count)}
                    >
                      {count}
                    </Button>
                  )
                })}
              </div>
            </>
          )}

          {phase === 'assignSlots' && (
            <>
              <Dialog.Title className="text-lg font-bold text-primary mb-6">Player Arrangement</Dialog.Title>

              <div className="mb-4">
                <div className="flex divide-x divide-basic">
                  {groups.slots.map((slot, slotIndex) => (
                    <button
                      key={slotIndex}
                      type="button"
                      className="flex-1 px-3 py-3 text-left"
                      onClick={
                        isLocalSharedScreen
                          ? undefined
                          : () => {
                              void startGameSetup.assignSelectedToSlot(slotIndex)
                            }
                      }
                    >
                      <div className="flex flex-wrap gap-2">
                        {slot.map(renderMateBadge)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

               <div className="mb-6">
                 <button
                   type="button"
                   className="w-full border border-basic rounded-md p-3 h-24 overflow-auto text-left"
                   onClick={
                     isLocalSharedScreen
                       ? undefined
                       : () => {
                           void startGameSetup.assignSelectedToObservers()
                         }
                   }
                 >
                   <div className="text-xs text-secondary mb-2">Just watching</div>
                   <div className="flex flex-wrap gap-2">
                     {groups.observers.map(renderMateBadge)}
                   </div>
                 </button>
               </div>

              <Button
                className="w-full"
                disabled={!startGameSetup.isValidArrangement.value}
                onClick={() => void startGameSetup.startGame()}
              >
                Start
              </Button>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

